 
import { addPayloadTransformationText, flowBuilderConstants } from '../../../constants/FlowBuilder';
import CanvasText from '../canvas-text/CanvasText';
import ConnectorLine, { ConnectorLineBtn } from '../connector-line/ConnectorLine';
import { drawCanvasImage, setImageSrc } from '../helpers';
import { CanvasConnectorIcon, CanvasElementBtn, CanvasElementConfig, ConnectorIcon, FlowButton, PositionWithSize } from '../TypeDefs';
import { drawBody, drawButtons, drawConditionCard, drawDescriptionText, drawIncomingHorizontalConnector, drawIncomingVerticalConnector, drawNameText, drawOutgoingHorizontalConnector, drawOutgoingVerticalConnector, drawPayloadTransformationWarning, drawVersionText } from './helpers';
import { Orientation } from './Orientation';

import payloadIcon from '../../../assets/images/AddPayload.svg';
import alertIcon from '../../../assets/images/AlertCircle.png';

export class CanvasElement {
  // variables required for creating instance
  public elementId = '';

  public rev?: number;

  public left = 0;

  public top = 0;

  public isSelected = false;

  protected icon = '';

  protected selectedIcon = '';

  protected isRoot = false;

  protected isError = false;

  protected isStackStyled = false;

  public conditionText?: string;

  // other instance variables

  private dragged = 0;

  public incomingHX = 0;

  public incomingHY = 0;

  public outgoingHX = 0;

  public outgoingHY = 0;

  public incomingVX = 0;

  public incomingVY = 0;

  public outgoingVX = 0;

  public outgoingVY = 0;

  protected right = 0;

  private bottom = 0;

  private dragOffsetX = 0;

  private dragOffsetY = 0;

  private incomingConnectors: ConnectorLine[] = [];

  private outgoingConnectors: ConnectorLine[] = [];

  protected mName = new CanvasText();

  protected mDescription = new CanvasText();

  protected mVersion = new CanvasText();

  protected addPayloadText = new CanvasText();

  protected iconImage = new Image();

  protected errorImage = new Image();

  protected payloadImage = new Image();

  public finalBottom = 0;

  private hovering = false;

  public payloadTransformSelected?: boolean;

  private btnGroupPosition: PositionWithSize = {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    height: 0,
    width: 0
  };

  protected buttons: CanvasElementBtn[] = [];

  protected incomingIcon?: CanvasConnectorIcon;

  protected outgoingIcon?: CanvasConnectorIcon;

  public conditionBoxPosition = {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  };

  public static create({
    elementId,
    rev,
    left,
    top,
    name,
    description = '',
    displayVersion = '',
    isSelected = false,
    icon,
    selectedIcon,
    isRoot = false,
    isError = false,
    isStackStyled = false,
    conditionText,
    incomingIcon,
    outgoingIcon,
    buttons = [],
    config,
    payloadTransformSelected
  }: {
    elementId: string,
    rev?: number,
    left: number,
    top: number,
    name: string,
    description?: string,
    displayVersion?: string,
    isSelected?: boolean,
    icon: string,
    selectedIcon: string,
    isRoot?: boolean,
    isError?: boolean,
    isStackStyled?: boolean,
    conditionText?: string,
    incomingIcon?: ConnectorIcon,
    outgoingIcon?: ConnectorIcon,
    buttons?: FlowButton[],
    config: CanvasElementConfig,
    payloadTransformSelected?: boolean
  }) {
    const instance = new CanvasElement(config);
    instance.elementId = elementId;
    instance.rev = rev;
    instance.left = left;
    instance.top = top;
    instance.isSelected = isSelected;
    instance.isRoot = isRoot;
    instance.isError = isError;
    instance.isStackStyled = isStackStyled;
    instance.conditionText = conditionText;
    instance.icon = icon;
    instance.selectedIcon = selectedIcon;

    buttons.forEach((b) => {
      const img = new Image();
      setImageSrc(img, b.icon);
      instance.buttons.push({
        ...b,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        image: img
      });
    });

    instance.calculateBound();

    instance.mName.text = name;
    instance.mDescription.text = description;
    instance.mVersion.text = displayVersion;
    instance.addPayloadText.text = addPayloadTransformationText;
    instance.payloadTransformSelected = payloadTransformSelected;
    setImageSrc(instance.iconImage, isSelected ? selectedIcon : icon);
    setImageSrc(instance.errorImage, alertIcon);
    setImageSrc(instance.payloadImage, payloadIcon);

    if (incomingIcon) {
      const image = new Image();
      setImageSrc(image, incomingIcon.src);
      instance.incomingIcon = {
        ...incomingIcon,
        image
      };
    }
    if (outgoingIcon) {
      const image = new Image();
      setImageSrc(image, outgoingIcon.src);
      instance.outgoingIcon = {
        ...outgoingIcon,
        image
      };
    }

    return instance;
  }

  public constructor(
    public config: CanvasElementConfig
  ) {
    // dummy 1 line to avoid blank constructor
    this.config = config;
  }

  get description() {
    return this.mDescription.text;
  }

  set description(newDesc: string) {
    this.mDescription.text = newDesc;
  }

  get name() {
    return this.mName.text;
  }

  set name(newName: string) {
    this.mName.text = newName;
  }

  get displayVersion() {
    return this.mVersion.text;
  }

  get displayPayloadText() {
    return this.addPayloadText.text;
  }

  /**
   * @method calculateBound
   * @description Calculate different points for the shape
   */
  protected calculateBound() {
    this.right = this.left + this.config.width;
    this.bottom = this.top + this.config.height;

    this.incomingHX = this.left;
    this.incomingHY = this.top + this.config.height / 2;

    this.incomingVX = this.left + this.config.width / 2;
    this.incomingVY = this.top;

    this.outgoingHX = this.right;
    this.outgoingHY = this.top + this.config.height / 2;

    this.outgoingVX = this.left + this.config.width / 2;
    this.outgoingVY = this.bottom;

    if (this.buttons.length > 0) {
      this.btnGroupPosition.width = this.buttons.reduce((p, c) => (p + c.width + 12), 12);
      this.btnGroupPosition.height = this.buttons[0].height + 16;
      this.btnGroupPosition.right = this.right - 10;
      this.btnGroupPosition.bottom = this.top;
      this.btnGroupPosition.top = this.top - this.btnGroupPosition.height;
      this.btnGroupPosition.left = this.btnGroupPosition.right - this.btnGroupPosition.width;
    }

    this.conditionBoxPosition.left = this.left - flowBuilderConstants.conditionTextBoxOffset.x;
    this.conditionBoxPosition.right = this.conditionBoxPosition.left + flowBuilderConstants.conditionTextBoxSize.w;
    this.conditionBoxPosition.top = this.incomingHY - flowBuilderConstants.conditionTextBoxOffset.y;
    this.conditionBoxPosition.bottom = this.conditionBoxPosition.top + flowBuilderConstants.conditionTextBoxSize.h;
  }

  /**
   * @method isMouseOnIncomingConnector
   * @description Check if mouse pointer is on incoming connector
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnIncomingConnector(
    mouseX: number,
    mouseY: number,
  ): Orientation | null {
    if (this.hasConvergence()) {
      if (
        Math.abs(mouseX - this.conditionBoxPosition.left) <= this.config.inConnectorRadius
        && Math.abs(mouseY - this.incomingHY) <= this.config.inConnectorRadius
      ) {
        return Orientation.HORIZONTAL;
      }
      return null;
    }

    if (
      this.config.orientation === Orientation.BOTH
      || this.config.orientation === Orientation.HORIZONTAL
    ) {
      if (
        Math.abs(mouseX - this.incomingHX) <= this.config.inConnectorRadius
        && Math.abs(mouseY - this.incomingHY) <= this.config.inConnectorRadius
      ) {
        return Orientation.HORIZONTAL;
      }
    }

    if (
      this.config.orientation === Orientation.BOTH
      || this.config.orientation === Orientation.VERTICAL
    ) {
      if (
        Math.abs(mouseX - this.incomingVX) <= this.config.inConnectorRadius
        && Math.abs(mouseY - this.incomingVY) <= this.config.inConnectorRadius
      ) {
        return Orientation.VERTICAL;
      }
    }
    return null;
  }

  /**
   * @method isMouseOnOutgoingConnector
   * @description Check if mouse pointer is on outgoing connector
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnOutgoingConnector(
    mouseX: number,
    mouseY: number,
  ): Orientation | null {
    if (
      this.config.orientation === Orientation.BOTH
      || this.config.orientation === Orientation.HORIZONTAL
    ) {
      if (
        Math.abs(mouseX - this.outgoingHX) <= this.config.outConnectorRadius + 2
        && Math.abs(mouseY - this.outgoingHY) <= this.config.outConnectorRadius + 2
      ) {
        return Orientation.HORIZONTAL;
      }
    }

    if (
      this.config.orientation === Orientation.BOTH
      || this.config.orientation === Orientation.VERTICAL
    ) {
      if (
        Math.abs(mouseX - this.outgoingVX) <= this.config.outConnectorRadius + 2
        && Math.abs(mouseY - this.outgoingVY) <= this.config.outConnectorRadius + 2
      ) {
        return Orientation.VERTICAL;
      }
    }
    return null;
  }

  /**
   * @method isMouseOnBody
   * @description Check if mouse is on item body
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnBody(mouseX: number, mouseY: number): boolean {
    return (
      mouseX >= this.left
      && mouseX <= this.right
      && mouseY >= this.top
      && mouseY <= this.finalBottom
    );
  }

  /**
   * @method isMouseOnCondition
   * @description Check if mouse is on ConditionBox (AND/OR)
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnCondition(mouseX: number, mouseY: number): boolean {
    return (
      this.hasConvergence()
      && mouseX >= this.conditionBoxPosition.left
      && mouseX <= this.conditionBoxPosition.right
      && mouseY >= this.conditionBoxPosition.top
      && mouseY <= this.conditionBoxPosition.bottom
    );
  }

  /**
   * @method isMouseOnBtnGroupBody
   * @description Check if mouse is on Button Group (Create CEM/Edit CEM, delete)
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnBtnGroupBody(mouseX: number, mouseY: number): boolean {
    return (
      this.hovering
      && mouseX >= this.btnGroupPosition.left
      && mouseX <= this.btnGroupPosition.right
      && mouseY >= this.btnGroupPosition.top
      && mouseY <= this.btnGroupPosition.bottom
    );
  }

  /**
   * @method performHoverEffect
   * @description perform Hovering Effect
   */
  performHoverEffect() {
    this.hovering = true;
  }

  /**
   * @method removeHoverEffect
   * @description remove Hovering Effect
   */
  removeHoverEffect() {
    this.hovering = false;
  }

  /**
   * @method mouseOnWhichConnectorLine
   * @description Find mouse on which connector line
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns connector line or null
   */
  mouseOnWhichConnectorLine(
    mouseX: number,
    mouseY: number,
  ): ConnectorLine | null {
    return this.incomingConnectors.find((c) => c.isMouseOnBody(mouseX, mouseY)) ?? null;
  }

  /**
   * @method mouseOnWhichConnectorText
   * @description Find mouse on which connector Text
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns connector line or null
   */
  mouseOnWhichConnectorText(
    mouseX: number,
    mouseY: number,
  ): ConnectorLine | null {
    return this.incomingConnectors.find((c) => c.isMouseOnText(mouseX, mouseY)) ?? null;
  }

  /**
   * @method mouseOnWhichConnectorBtn
   * @description Find mouse on which connector Button
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns connector line or null
   */
  mouseOnWhichConnectorBtn(
    mouseX: number,
    mouseY: number,
  ): { line: ConnectorLine, btn: ConnectorLineBtn } | null {
    let btn: ConnectorLineBtn | null = null;
    const line = this.incomingConnectors.find((l) => {
      btn = l.mouseOnWhichButton(mouseX, mouseY);
      return btn;
    });
    return (line && btn) ? { line, btn } : null;
  }

  /**
   * @method mouseOnWhichButton
   * @description Find mouse on which Button in Canvas
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns canvasElement Button or null
   */
  mouseOnWhichButton(
    mouseX: number,
    mouseY: number,
  ): CanvasElementBtn | null {
    if (!this.hovering) {
      return null;
    }
    return this.buttons.find((b) => (
      mouseX >= b.left - 6
      && mouseX <= b.right + 6
      && mouseY >= b.top - 6
      && mouseY <= b.bottom + 6
    )) ?? null;
  }

  /**
   * @method dragTo
   * @description Adjust item location based on mouse position
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   */
  dragTo(mouseX: number, mouseY: number) {
    const newX = mouseX - this.dragOffsetX;
    const newY = mouseY - this.dragOffsetY;
    this.dragged += newX - this.left + (newY - this.top);
    this.left = newX;
    this.top = newY;

    this.calculateBound();
  }

  /**
   * @method drag
   * @description Adjust item location based on mouse movement delta
   * @param movementX mouse x movement delta
   * @param movementY mouse y position delta
   */
  drag(movementX: number, movementY: number) {
    this.left += movementX;
    this.top += movementY;

    this.calculateBound();
  }

  /**
   * @method dragStart
   * @description Start dragging to remember mouse offset from item top left position to avoid jumping
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   */
  dragStart(mouseX: number, mouseY: number) {
    this.dragOffsetX = mouseX - this.left;
    this.dragOffsetY = mouseY - this.top;
    this.dragged = 0;
  }

  /**
   * @method hasDragged
   * @description Check if item has been dragged or not
   * @returns true / false
   */
  hasDragged() {
    return Math.round(this.dragged) > 30;
  }

  /**
   * @method select
   * @description Mark item as selected
   */
  select() {
    this.isSelected = true;
    setImageSrc(this.iconImage, this.selectedIcon);
  }

  /**
   * @method deselect
   * @description Mark item and all incoming connectors as not selected
   */
  deselect() {
    this.isSelected = false;
    setImageSrc(this.iconImage, this.icon);
    this.incomingConnectors.forEach((l) => l.deselect());
  }

  /**
   * @method isConnectorSelected
   * @description Check if any incoming connector is selected
   * @returns true / false
   */
  isConnectorSelected() {
    let ret = false;
    this.incomingConnectors.every((line) => {
      if (line.isSelected) {
        ret = true;
        return false;
      }
      return true;
    });
    return ret;
  }

  /**
   * @method addIncomingConnector
   * @description Add line to incoming list
   * @param connector connector line
   */
  addIncomingConnector(connector: ConnectorLine) {
    this.incomingConnectors.push(connector);
  }

  /**
   * @method addOutgoingConnector
   * @description Add line to outgoing list
   * @param connector connector line
   */
  addOutgoingConnector(connector: ConnectorLine) {
    this.outgoingConnectors.push(connector);
  }

  /**
   * @method clearConnectors
   * @description Clear all connectors
   */
  clearConnectors() {
    this.incomingConnectors = [];
    this.outgoingConnectors = [];
  }

  /**
   * @method draw
   * @description Draw the item on canvas
   * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
   */
  draw(ctx: CanvasRenderingContext2D) {
    const nameFont = `600 ${this.config.nameFontSize}px '${this.config.fontFamily}'`;
    const descriptionFont = `400 ${this.config.descriptionFontSize}px '${this.config.fontFamily}'`;
    const payloadFont = `400 ${this.config.versionFontSize}px '${this.config.fontFamily}'`;
    const versionFont = `400 ${this.config.versionFontSize}px '${this.config.fontFamily}'`;
    const nameLineHeight = this.config.nameFontSize * this.config.lineHeightRatio;
    const descriptionLineHeight = this.config.descriptionFontSize * this.config.lineHeightRatio;
    const payloadTextLineHeight = this.config.descriptionFontSize * this.config.lineHeightRatio;
    const versionLineHeight = this.config.versionFontSize * this.config.lineHeightRatio;

    const centerY = this.top + this.config.height / 2;
    const iconX = this.left + this.config.padding;
    const iconSize = this.isSelected ? (nameLineHeight * 2) : (nameLineHeight + 10);
    const alertX = this.right - iconSize / 2;
    const alertY = this.top - iconSize / 2;
    const nameTextX = iconX + iconSize + this.config.padding;

    // draw the buttons on hover
    if (this.hovering) {
      drawButtons({
        btnGroupPosition: this.btnGroupPosition,
        buttons: this.buttons,
        config: this.config,
        ctx
      });
    }

    // prepare the description before hand so that, expanded height can be calculated
    if (this.mDescription.text.length > 0) {
      ctx.font = descriptionFont;
      this.mDescription.wrapText(
        ctx,
        this.config.width - (nameTextX - this.left) - this.config.padding * 2,
      );
    }

    ctx.font = payloadFont;
    this.addPayloadText.wrapText(
      ctx,
      this.config.width - (nameTextX - this.left) - this.config.padding * 2,
    );

    const payloadTransformWarn = (this.conditionText === 'AND' || this.conditionText === 'T-AND') && !this.payloadTransformSelected;

    // draw element body
    const { height } = drawBody({
      ctx,
      cardRadius: flowBuilderConstants.elementCornerRadius,
      config: this.config,
      descriptionLineHeight,
      drawingStep: false,
      isError: this.isError,
      isSelected: this.isSelected,
      isStackStyled: this.isStackStyled,
      left: this.left,
      mDescription: this.mDescription,
      top: this.top,
      conditionText: this.conditionText,
      payloadTransformWarn
    });
    this.finalBottom = this.top + height;
    this.outgoingVY = this.finalBottom;

    if (
      this.config.orientation === Orientation.HORIZONTAL
      || this.config.orientation === Orientation.BOTH
    ) {
      // Horizontal connectors
      // draw incoming connector
      drawIncomingHorizontalConnector({
        ctx,
        config: this.config,
        incomingConnectors: this.incomingConnectors,
        incomingHX: this.incomingHX,
        incomingHY: this.incomingHY,
        isRoot: this.isRoot,
        isSelected: this.isSelected,
        incomingIcon: this.incomingIcon,
      });

      // draw outgoing connector
      drawOutgoingHorizontalConnector({
        ctx,
        config: this.config,
        isSelected: this.isSelected,
        outgoingHX: this.outgoingHX,
        outgoingHY: this.outgoingHY,
        outgoingIcon: this.outgoingIcon,
      });
    }

    if (
      this.config.orientation === Orientation.VERTICAL
      || this.config.orientation === Orientation.BOTH
    ) {
      // Vertical connectors
      // draw incoming connector
      drawIncomingVerticalConnector({
        ctx,
        config: this.config,
        incomingConnectors: this.incomingConnectors,
        incomingVX: this.incomingVX,
        incomingVY: this.incomingVY,
        isRoot: this.isRoot,
        isSelected: this.isSelected,
        incomingIcon: this.incomingIcon,
      });

      // draw outgoing connector
      drawOutgoingVerticalConnector({
        ctx,
        config: this.config,
        isSelected: this.isSelected,
        outgoingVX: this.outgoingVX,
        outgoingVY: this.outgoingVY,
        outgoingIcon: this.outgoingIcon,
      });
    }

    const iconY = this.top + height / 2 - iconSize / 2;

    // draw the icon
    drawCanvasImage(ctx, this.iconImage, iconX, iconY, iconSize, iconSize);
    if (this.isError) {
      drawCanvasImage(ctx, this.errorImage, alertX, alertY, iconSize, iconSize);
    }

    if (payloadTransformWarn) {
      drawCanvasImage(ctx, this.payloadImage, nameTextX, centerY - payloadTextLineHeight - 4, nameLineHeight - 7, nameLineHeight - 7);
    }

    // draw the name
    drawNameText({
      centerY,
      config: this.config,
      ctx,
      isSelected: this.isSelected,
      left: this.left,
      mName: this.mName,
      nameFont,
      nameTextX,
      nameLineHeight,
      payloadTransformWarn
    });

    // draw the version
    drawVersionText({
      centerY,
      config: this.config,
      ctx,
      isSelected: this.isSelected,
      left: this.left,
      mVersion: this.mVersion,
      versionFont,
      versionTextX: nameTextX,
      versionLineHeight,
      payloadTransformWarn
    });

    // render the description only when selected
    drawDescriptionText({
      ctx,
      descriptionFont,
      nameTextX,
      centerY,
      descriptionLineHeight,
      config: this.config,
      isSelected: this.isSelected,
      payloadTransformWarn,
      mDescription: this.mDescription
    });

    drawPayloadTransformationWarning({
      ctx,
      payloadFont,
      nameTextX,
      centerY,
      payloadLineHeight: payloadTextLineHeight,
      config: this.config,
      payloadTransformWarn,
      addPayloadText: this.addPayloadText,
      nameLineHeight
    });

    drawConditionCard({
      ctx,
      conditionBoxPosition: this.conditionBoxPosition,
      config: this.config,
      hasConvergence: this.hasConvergence(),
      incomingHX: this.incomingHX,
      incomingHY: this.incomingHY,
      conditionText: this.conditionText
    });
  }

  /**
   * @method drawConnectors
   * @description Draw connectors on canvas
   * @param ctx canvas 2d context
   */
  drawConnectors(ctx: CanvasRenderingContext2D) {
    this.incomingConnectors.forEach((l) => l.draw(ctx));
  }

  /**
   * @method hasConvergence
   * @description Check if element has a convergence [multiple incoming connector]
   */
  hasConvergence() {
    return this.incomingConnectors.length > 1;
  }

  /**
   * @method removeConnectorHoverEffect
   * @description Remove connector hovering effect
   */
  removeConnectorHoverEffect() {
    this.incomingConnectors.forEach((l) => l.removeHoverEffect());
  }

  /**
   * @method mouseOnWhichConnectorBtnGroupBody
   * @description Remove connector hovering effect
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns connector line or null
   */
  mouseOnWhichConnectorBtnGroupBody(mouseX: number, mouseY: number): ConnectorLine | null {
    return this.incomingConnectors.find((c) => c.isMouseOnBtnGroupBody(mouseX, mouseY)) ?? null;
  }

  /**
   * @method drawIntermediateConnector
   * @description Draw the dotted line from element to current mouse position
   * @param ctx canvas context
   * @param isIncoming if drawing from in connector
   * @param mouseX mouse x position relative to canvas
   * @param mouseY mouse y position relative to canvas
   */
  drawIntermediateConnector(
    ctx: CanvasRenderingContext2D,
    isIncoming: boolean,
    mouseX: number,
    mouseY: number
  ) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'black';
    ctx.beginPath();
    ctx.setLineDash([5, 15]);

    if (this.hasConvergence() && isIncoming) {
      ctx.moveTo(this.conditionBoxPosition.left, this.incomingHY);
    } else if (isIncoming) {
      ctx.moveTo(
        this.config.orientation === Orientation.HORIZONTAL ? this.incomingHX : this.incomingVX,
        this.config.orientation === Orientation.HORIZONTAL ? this.incomingHY : this.incomingVY
      );
    } else {
      ctx.moveTo(
        this.config.orientation === Orientation.HORIZONTAL ? this.outgoingHX : this.outgoingVX,
        this.config.orientation === Orientation.HORIZONTAL ? this.outgoingHY : this.outgoingVY
      );
    }

    ctx.lineTo(mouseX, mouseY);
    ctx.stroke();
  }
}
